import React, { useState } from "react";
import {
  GameCondition,
  Event,
  Event_Type,
} from "../api/copenhunt/v1/copenhunt_pb";
import CountdownCircle from "./CountdownCircle";
import "../styles/CountdownCircle.css";

interface GameOverviewProps {
  condition: GameCondition | undefined;
  secondsToNextSwitch: number | undefined;
  isPaused: boolean | undefined;
  isAdmin?: boolean;
  totalSeconds?: number;
  secondsToNextPing?: number;
  pingIntervalSeconds?: number;
  hunterPingsRemaining?: number;
  events?: Event[];
  currentTimestamp?: number;
  latestPingTimestamp?: number;
}

const GameOverview: React.FC<GameOverviewProps> = ({
  condition,
  secondsToNextSwitch,
  isPaused,
  isAdmin = false,
  totalSeconds,
  secondsToNextPing,
  pingIntervalSeconds,
  hunterPingsRemaining,
  events,
  currentTimestamp,
  latestPingTimestamp,
}) => {
  const label =
    condition !== undefined &&
    condition !== GameCondition.DAY &&
    condition !== GameCondition.NIGHT
      ? GameCondition[condition]
      : "";

  const latestPingEvent = events
    ?.filter((e) => e.type === Event_Type.HUNTERS_PINGED)
    .sort((a, b) => b.timestamp - a.timestamp)[0];

  const adminEvents = events
    ?.filter((e) => e.type === Event_Type.MESSAGE_FROM_ADMIN)
    .sort((a, b) => b.timestamp - a.timestamp);

  const [dismissedAdminMessages, setDismissedAdminMessages] = useState<
    number[]
  >([]);

  const handleDismissAdminMessage = (timestamp: number) => {
    setDismissedAdminMessages((prev) => [...prev, timestamp]);
  };

  const showPingNotification =
    latestPingEvent && (latestPingTimestamp ?? 0) <= latestPingEvent.timestamp;

  const pingSecondsAgo =
    (currentTimestamp ?? 0) - (latestPingEvent?.timestamp ?? 0);

  return (
    <div className="overview-container">
      {showPingNotification && latestPingEvent && (
        <div className="hunter-ping-notification">
          {latestPingEvent.text} {pingSecondsAgo} seconds ago
        </div>
      )}
      {adminEvents?.map(
        (event) =>
          !dismissedAdminMessages.includes(event.timestamp) && (
            <div className="admin-message-notification" key={event.timestamp}>
              {event.text}
              <button
                className="dismiss-button"
                onClick={() => handleDismissAdminMessage(event.timestamp)}
              >
                &times;
              </button>
            </div>
          )
      )}
      <div className={`overview ${isAdmin ? "admin-overview" : ""}`}>
        <div className="countdown-wrapper">
          {totalSeconds !== undefined && secondsToNextSwitch !== undefined ? (
            <>
              <CountdownCircle
                secondsRemaining={secondsToNextSwitch}
                totalSeconds={totalSeconds}
                isPaused={isPaused ?? false}
                label={label}
              />
              <span className="countdown-title">Game Time</span>
            </>
          ) : (
            <p>
              <span className="label">Countdown:</span>{" "}
              <span className="value">—</span>
            </p>
          )}
        </div>
        <div className="countdown-wrapper">
          {pingIntervalSeconds !== undefined &&
          secondsToNextPing !== undefined ? (
            <>
              <CountdownCircle
                secondsRemaining={secondsToNextPing}
                totalSeconds={pingIntervalSeconds}
                isPaused={isPaused ?? false}
                label={label}
              />
              <span className="countdown-title">Next Ping</span>
            </>
          ) : (
            <p>
              <span className="label">Next Ping:</span>{" "}
              <span className="value">—</span>
            </p>
          )}
        </div>
      </div>
      {isAdmin && hunterPingsRemaining !== undefined && (
        <div className="hunter-pings-info">
          {hunterPingsRemaining} hunter pings left
        </div>
      )}
    </div>
  );
};

export default GameOverview;
